import type { Provider } from "../types";
import { PROVIDER_CREDENTIAL_FIELDS } from "../types";
import { ProviderBadge } from "./ProviderBadge";

interface CredentialEntryProps {
  provider: Provider;
  credentials: Record<string, string>;
  submitting: boolean;
  isSandbox: boolean;
  onChangeCredential: (field: string, value: string) => void;
  onSubmit: () => void;
}

export function CredentialEntry({
  provider,
  credentials,
  submitting,
  isSandbox,
  onChangeCredential,
  onSubmit,
}: CredentialEntryProps) {
  const fields = PROVIDER_CREDENTIAL_FIELDS[provider.id] || [];
  const allFilled = fields.every((f) => credentials[f.field] && credentials[f.field].trim().length > 0);

  return (
    <div>
      <div className="flex items-center gap-3">
        <ProviderBadge provider={provider} />
        <h1 className="text-base font-semibold text-gray-900">Log into {provider.name}</h1>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (allFilled) onSubmit();
        }}
        className="mt-5 space-y-4"
      >
        {fields.map((field) => (
          <div key={field.field}>
            <label htmlFor={field.field} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <input
              id={field.field}
              type={field.type === "password" ? "password" : "text"}
              value={credentials[field.field] || ""}
              onChange={(e) => onChangeCredential(field.field, e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-gray-500"
              placeholder={isSandbox ? "any value (sandbox)" : field.label}
              autoComplete="off"
            />
          </div>
        ))}

        <p className="text-xs text-gray-400">
          Credentials are encrypted (AES-256-GCM) and never returned in API responses.
        </p>

        <button
          type="submit"
          disabled={!allFilled || submitting}
          className="w-full py-2.5 bg-gray-900 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800"
        >
          {submitting ? "Connecting…" : "Continue to login"}
        </button>
      </form>
    </div>
  );
}
